import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'

export interface SeasonSummaryCardProps {
  huntsAttended: number
  hoursInField: number
  gameReported: number
  downed: number
  observed: number
  /**
   * Wounded-and-not-recovered reports. Shown as its own figure because it used to
   * be counted inside `observed`, which both overstated observations and hid the
   * outstanding tracking work — and because `gameReported` includes it, so
   * omitting it here would leave a breakdown that does not add up to the headline.
   */
  wounded: number
  /**
   * The figures could not be loaded. Renders a message and a retry instead of the
   * numbers, because zeros here read as a factual "you did nothing this season"
   * — the card cannot show a failure and a total in the same place.
   */
  loadFailed?: boolean
  onRetry?: () => void
}

export function SeasonSummaryCard({
  huntsAttended,
  hoursInField,
  gameReported,
  downed,
  observed,
  wounded,
  loadFailed = false,
  onRetry,
}: SeasonSummaryCardProps) {
  const { t } = useTranslation('overview')
  return (
    <div className="wh-card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.outline, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
        {t('season.title')}
      </div>
      {loadFailed ? (
        <div role="alert" style={{ fontSize: 13, color: COLORS.onSurface }}>
          <div>{t('season.loadError')}</div>
          {onRetry ? (
            <button type="button" className="wh-btn-text" onClick={onRetry} style={{ marginTop: 8 }}>
              {t('season.retry')}
            </button>
          ) : null}
        </div>
      ) : (
      <div className="wh-season-stats">
        <div className="wh-season-stat">
          <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.onSurface }}>{huntsAttended}</div>
          <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>{t('season.huntsAttended')}</div>
        </div>
        <div className="wh-season-stat">
          <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.onSurface }}>{hoursInField}</div>
          <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>{t('season.hoursInField')}</div>
        </div>
        <div className="wh-season-stat">
          <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.onSurface }}>{gameReported}</div>
          <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>{t('season.gameReported')}</div>
          <div style={{ fontSize: 11, color: COLORS.outline, marginTop: 1 }}>
            {wounded > 0
              ? t('season.downedObservedWounded', { downed, observed, wounded })
              : t('season.downedObserved', { downed, observed })}
          </div>
        </div>
      </div>
      )}
    </div>
  )
}
